/**
 * useNoteGenerationStreaming
 *
 * Hook for handling SSE streaming of note generation v2 workflow.
 * Connects to the encounter stream endpoint and parses XML sections as they arrive.
 *
 * Based on patterns from:
 * - useStreamingSections (jotdocs) - XML section parsing
 * - streamingApi.ts (note-ai-edit) - SSE connection with auth
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useStreamingSections } from '@features/jotdocs';
import type { ParsedSection } from '@features/jotdocs/utils/markdown-utils';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import SentryService from '@services/SentryService';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Timeout for streaming (5 minutes)
const STREAM_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * AG-UI Protocol event types for note generation workflow
 */
type AGUIEventType =
  | 'RUN_STARTED'
  | 'RUN_FINISHED'
  | 'STEP_STARTED'
  | 'STEP_FINISHED'
  | 'TEXT_MESSAGE_START'
  | 'TEXT_MESSAGE_CONTENT'
  | 'TEXT_MESSAGE_END'
  | 'ACTIVITY_SNAPSHOT'
  | 'ERROR';

export interface WorkflowNode {
  id: string;
  displayName: string;
  visible: boolean;
  order: number;
}

interface WorkflowManifest {
  workflowName: string;
  nodes: WorkflowNode[];
  totalSteps: number;
}

/**
 * Encounter analysis content structure from ACTIVITY_SNAPSHOT events
 */
export interface EncounterAnalysis {
  summary?: string;
  semantic_tags?: string[];
  transcript_origin?: string;
  session_type?: {
    canonical: string;
    clinic_terms?: string[];
  };
  place_of_care?: string;
  encounter_mode?: string;
  participants?: Array<{
    role: string;
    count: number;
    notes?: string;
  }>;
  modalities?: string[];
  activities_performed?: string[];
  treatment_facts?: {
    medications?: any[];
    supplements?: any[];
    procedures?: any[];
  };
  quantitative_clinical_markers?: {
    vitals?: any[];
    scales?: any[];
    labs?: any[];
    device_metrics?: any[];
  };
  cpt_code_suggested?: {
    primary: string;
    add_ons?: string[];
    modifiers?: string[];
    e_m_coding_basis?: string;
    confidence?: number;
    rationale?: string;
  };
  complexity?: {
    level: string;
    basis?: string;
    rationale?: string;
  };
}

interface AGUIEvent {
  type: AGUIEventType;
  runId?: string;
  threadId?: string;
  stepName?: string;
  status?: string;
  messageId?: string;
  role?: string;
  namespace?: string;
  delta?: string;
  message?: string;
  timestamp?: number;
  manifest?: WorkflowManifest;
  activityType?: string;
  replace?: boolean;
  content?: EncounterAnalysis;
}

export interface UseNoteGenerationStreamingOptions {
  /** Encounter ID to connect to the stream endpoint */
  encounterId?: string;
  /** Thread ID for status hydration (optional - enables mid-stream join) */
  threadId?: string;
  /** Note ID for cache invalidation */
  noteId: string;
  /** Callback when streaming completes */
  onComplete: () => void;
  /** Skip the streaming connection */
  skip?: boolean;
}

export interface UseNoteGenerationStreamingResult {
  /** Parsed sections from the stream */
  sections: ParsedSection[];
  /** Whether actively receiving stream data */
  isStreaming: boolean;
  /** Whether the stream has completed */
  isComplete: boolean;
  /** Current workflow step name */
  workflowStep: string | null;
  /** List of completed workflow steps */
  completedSteps: string[];
  /** Workflow nodes from manifest (for dynamic progress display) */
  workflowNodes: WorkflowNode[];
  /** Error message if stream failed */
  error: string | null;
  /** Encounter analysis data from ACTIVITY_SNAPSHOT event */
  encounterAnalysis: EncounterAnalysis | null;
}

/**
 * Hook for streaming note generation via SSE.
 *
 * Connects to /v2/provider/encounters/{encounterId}/stream and processes
 * AG-UI protocol events to show real-time note generation.
 */
export function useNoteGenerationStreaming(
  options: UseNoteGenerationStreamingOptions,
): UseNoteGenerationStreamingResult {
  const { encounterId, threadId, noteId, onComplete, skip = false } = options;

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [workflowStep, setWorkflowStep] = useState<string | null>(null);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [workflowNodes, setWorkflowNodes] = useState<WorkflowNode[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [encounterAnalysis, setEncounterAnalysis] = useState<EncounterAnalysis | null>(null);

  // Use the shared streaming sections hook for XML parsing
  const {
    sections,
    processDelta,
    markComplete: markSectionsComplete,
    reset: resetSections,
  } = useStreamingSections();

  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasConnectedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  // Track which note we're streaming for to detect navigation between notes
  const currentNoteIdRef = useRef<string | null>(null);

  // Keep onComplete ref up to date
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Reset state when encounterId changes
  const resetState = useCallback(() => {
    setIsStreaming(false);
    setIsComplete(false);
    setWorkflowStep(null);
    setCompletedSteps([]);
    setWorkflowNodes([]);
    setError(null);
    resetSections();
    hasConnectedRef.current = false;
  }, [resetSections]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Handle stream completion
  const handleComplete = useCallback(() => {
    markSectionsComplete();
    setIsStreaming(false);
    setIsComplete(true);
    SentryService.setTag('encounterPhase', '');
    cleanup();
    onCompleteRef.current();
  }, [markSectionsComplete, cleanup]);

  // Connect to SSE stream
  useEffect(() => {
    // Skip if disabled or missing encounterId
    if (skip || !encounterId) {
      return;
    }

    // Check if we're on a different note than what we were streaming for
    // This handles navigation between notes from the same encounter
    const isNewNote = noteId !== currentNoteIdRef.current;
    if (isNewNote) {
      // Reset connection state for the new note
      hasConnectedRef.current = false;
      currentNoteIdRef.current = noteId;
    }

    // Prevent duplicate connections for the same note
    if (hasConnectedRef.current) {
      return;
    }

    hasConnectedRef.current = true;
    console.debug('[NoteGenerationStreaming] Connecting:', { encounterId, threadId, noteId });

    // Reset state for new connection
    resetState();
    hasConnectedRef.current = true; // Re-set after reset

    // Create abort controller for this connection
    abortControllerRef.current = new AbortController();

    // Set up timeout
    timeoutRef.current = setTimeout(() => {
      console.warn('[NoteGenerationStreaming] Stream timeout after 5 minutes');
      SentryService.logEvent('Note generation stream timed out', {
        level: 'error',
        extra: { noteId, threadId, timeoutMs: STREAM_TIMEOUT_MS },
      });
      setError('Note generation timed out. The note may still be processing.');
      cleanup();
      onCompleteRef.current();
    }, STREAM_TIMEOUT_MS);

    // Start streaming
    const startStreaming = async () => {
      try {
        // Get auth token
        const token = await getAuth0AccessTokenSilently();

        // Hydrate state from processing status if threadId is available
        // This allows joining mid-stream without missing the manifest and progress
        if (threadId) {
          try {
            const statusUrl = `${API_BASE_URL}/v2/provider/note-processing-workflows/status/${threadId}`;
            console.debug('[NoteGenerationStreaming] Fetching processing status:', statusUrl);

            const statusResponse = await fetch(statusUrl, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (statusResponse.ok) {
              const status = await statusResponse.json();
              console.debug('[NoteGenerationStreaming] Processing status received:', {
                status: status.status,
                current_step: status.current_step,
                completed_steps: status.completed_steps?.length,
                total_steps: status.total_steps,
              });

              // Hydrate workflow nodes (manifest)
              if (status.steps && Array.isArray(status.steps)) {
                const nodes: WorkflowNode[] = status.steps.map((s: any) => ({
                  id: s.id,
                  displayName: s.display_name,
                  visible: true,
                  order: s.order,
                }));
                setWorkflowNodes(nodes);
              }

              // Hydrate current step and completed steps
              if (status.current_step) {
                setWorkflowStep(status.current_step);
              }
              if (status.completed_steps && Array.isArray(status.completed_steps)) {
                setCompletedSteps(status.completed_steps);
              }

              // If already completed, skip SSE connection
              if (status.status === 'completed') {
                console.debug('[NoteGenerationStreaming] Status is completed, skipping SSE');
                handleComplete();
                return;
              }
            } else if (statusResponse.status === 204) {
              // 204 = No events yet, workflow may still be starting
              console.debug('[NoteGenerationStreaming] No status events yet (204), proceeding to SSE');
            } else {
              console.warn('[NoteGenerationStreaming] Failed to fetch status:', statusResponse.status);
              // Continue to SSE anyway - it will work even without hydration
            }
          } catch (err) {
            console.warn('[NoteGenerationStreaming] Error fetching status, continuing to SSE:', err);
            // Continue to SSE anyway - it will work even without hydration
          }
        }

        const streamUrl = `${API_BASE_URL}/v2/provider/encounters/${encounterId}/stream`;

        console.debug('[NoteGenerationStreaming] Connecting to stream:', streamUrl);

        const controller = abortControllerRef.current;
        if (!controller) {
          console.warn('[NoteGenerationStreaming] AbortController not available');
          return;
        }

        await fetchEventSource(streamUrl, {
          method: 'GET',
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },

          onopen: async response => {
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Stream connection failed: ${response.status} - ${errorText}`);
            }

            console.debug('[NoteGenerationStreaming] Stream connected');
            SentryService.setTag('encounterPhase', 'note-generation');
            setIsStreaming(true);
          },

          onmessage: event => {
            if (!event.data) return;

            try {
              const data = JSON.parse(event.data) as AGUIEvent;

              switch (data.type) {
                case 'RUN_STARTED':
                  console.debug('[NoteGenerationStreaming] Run started:', data.runId);
                  if (data.manifest?.nodes) {
                    // Sort nodes by order to ensure correct display order
                    const sortedNodes = [...data.manifest.nodes].sort((a, b) => a.order - b.order);
                    setWorkflowNodes(sortedNodes);
                    console.debug('[NoteGenerationStreaming] Workflow manifest received:', {
                      workflowName: data.manifest.workflowName,
                      totalSteps: data.manifest.totalSteps,
                      nodes: sortedNodes.length,
                    });
                  }
                  break;

                case 'STEP_STARTED':
                  if (data.stepName) {
                    console.debug('[NoteGenerationStreaming] Step started:', data.stepName);
                    setWorkflowStep(data.stepName);
                  }
                  break;

                case 'STEP_FINISHED':
                  if (data.stepName) {
                    const stepName = data.stepName;
                    console.debug('[NoteGenerationStreaming] Step finished:', stepName);
                    setCompletedSteps(prev => [...prev, stepName]);

                    // Check for finalize_note step completion
                    if (stepName === 'finalize_note') {
                      handleComplete();
                    }
                  }
                  break;

                case 'TEXT_MESSAGE_START':
                  // Check for document namespace to start accumulating content
                  if (data.namespace === 'document') {
                    console.debug('[NoteGenerationStreaming] Document message started');
                  }
                  break;

                case 'TEXT_MESSAGE_CONTENT':
                  // Append deltas from document namespace to section parser
                  if (data.namespace === 'document' && data.delta) {
                    processDelta(data.delta);
                  }
                  break;

                case 'TEXT_MESSAGE_END':
                  if (data.namespace === 'document') {
                    console.debug('[NoteGenerationStreaming] Document message ended');
                    // Don't mark complete here - wait for RUN_FINISHED or finalize_note
                  }
                  break;

                case 'RUN_FINISHED':
                  console.debug('[NoteGenerationStreaming] Run finished:', data.status);
                  handleComplete();
                  break;

                case 'ACTIVITY_SNAPSHOT':
                  if (data.activityType === 'ENCOUNTER_ANALYSIS' && data.content) {
                    console.debug('[NoteGenerationStreaming] Encounter analysis received:', data.content);
                    setEncounterAnalysis(data.content);
                  }
                  break;

                case 'ERROR':
                  console.error('[NoteGenerationStreaming] Stream error:', data.message);
                  SentryService.logEvent('Note generation stream error event', {
                    level: 'error',
                    extra: { noteId, threadId, message: data.message },
                  });
                  setError(data.message || 'An error occurred during note generation');
                  cleanup();
                  break;

                default:
                  // Log unhandled event types for debugging
                  console.debug('[NoteGenerationStreaming] Unhandled event:', data.type);
              }
            } catch {
              // JSON parse error - ignore malformed events
              console.debug('[NoteGenerationStreaming] Failed to parse event:', event.data);
            }
          },

          onerror: err => {
            console.error('[NoteGenerationStreaming] Stream error:', err);

            // Don't set error on abort (intentional disconnect)
            if (abortControllerRef.current?.signal.aborted) {
              return;
            }

            SentryService.captureException(err, {
              level: 'error',
              tags: { encounterPhase: 'note-generation', errorType: 'sse_disconnect' },
              extra: { noteId, threadId },
            });
            setError('Lost connection to note generation stream');
            cleanup();

            // Throw to prevent fetchEventSource from retrying
            throw err;
          },

          onclose: () => {
            console.debug('[NoteGenerationStreaming] Stream closed');

            // If we haven't completed yet, trigger completion
            if (!isComplete) {
              handleComplete();
            }
          },
        });
      } catch (err) {
        // Don't set error on abort (intentional disconnect)
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        console.error('[NoteGenerationStreaming] Failed to start streaming:', err);
        SentryService.captureException(err, {
          level: 'error',
          tags: { encounterPhase: 'note-generation', errorType: 'connection_failure' },
          extra: { noteId, threadId },
        });
        setError(
          err instanceof Error ? err.message : 'Failed to connect to note generation stream',
        );
        cleanup();
      }
    };

    startStreaming();

    // Cleanup on unmount or when dependencies change
    return () => {
      cleanup();
      SentryService.setTag('encounterPhase', '');
      // Reset connection flag so a new connection can be established
      // This is critical when navigating between notes (same component, different noteId)
      hasConnectedRef.current = false;
    };
  }, [encounterId, noteId, skip, resetState, processDelta, handleComplete, cleanup, isComplete]);

  return {
    sections,
    isStreaming,
    isComplete,
    workflowStep,
    completedSteps,
    workflowNodes,
    error,
    encounterAnalysis,
  };
}
